/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
import TabPanel from 'components/TabPanel/TabPanel';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initAttr = {
  id: '',
  name: '',
  name_en: '',
  logo: '',
  target_purchase: '',
  onoff: 'Y',
  pro_onoff: 'Y',
  contact_line: '',
  contact_email: '',
  contact_tel: '',
  limit_po_approve: '',
  quantity_export: '',
  quotation_remark: '',
  invoice_remark: '',
  vat: '',
  lineid: '',
  linelink: '',
  image_printtag: '',
  enable: 'Y',
  created_at: '',
  updated_at: '',
};

function Config() {
  const mod = 'config';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [attr, setAttr] = useState(initAttr);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const editAccount = () => {
    if (!Func.verifyLength(attr.vat, 1)) return;

    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('PATCH', `/${mod}/1`, app.token, {
          vat: attr.vat,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.${result.msg}`), 'error');
          }
        });
      }
    });
  };

  const editPurchase = () => {
    if (!Func.verifyLength(attr.limit_po_approve, 1)) return;
    if (!Func.verifyLength(attr.target_purchase, 1)) return;

    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('PATCH', `/${mod}/1`, app.token, {
          limit_po_approve: attr.limit_po_approve,
          target_purchase: attr.target_purchase,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.${result.msg}`), 'error');
          }
        });
      }
    });
  };

  const editWarehouse = () => {
    if (!Func.verifyLength(attr.quantity_export, 1)) return;

    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('PATCH', `/${mod}/1`, app.token, {
          quantity_export: attr.quantity_export,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.${result.msg}`), 'error');
          }
        });
      }
    });
  };

  const editSale = () => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('PATCH', `/${mod}/1`, app.token, {
          quotation_remark: attr.quotation_remark,
          invoice_remark: attr.invoice_remark,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.${result.msg}`), 'error');
          }
        });
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(result.data);
        setLoading(false);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderAccount = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h6' mb={2}>
          บัญชี
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='ภาษีมูลค่าเพิ่ม'
              onChange={(e) => setAttr({ ...attr, vat: e.target.value })}
              onFocus={(e) => e.target.select()}
              value={attr.vat}
              type='number'
              variant='standard'
              fullWidth
              InputProps={{
                inputProps: {
                  maxLength: 20,
                  autoComplete: 'off',
                  className: 'text-right',
                  placeholder: '0',
                },
                endAdornment: (
                  <InputAdornment position='end'>
                    <span>%</span>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <Grid container mt={3}>
          <Grid item xs={12} display='flex' justifyContent='flex-end' gap={1}>
            <MDButton variant='outlined' color='success' onClick={editAccount}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPurchase = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h6' mb={2}>
          จัดซื้อ
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='ปริมาณขั้นต่ำ ขออนุมัติสั่งซื้อ'
              onChange={(e) => setAttr({ ...attr, limit_po_approve: e.target.value })}
              onFocus={(e) => e.target.select()}
              value={attr.limit_po_approve}
              variant='standard'
              type='number'
              fullWidth
              InputProps={{
                inputProps: {
                  maxLength: 20,
                  autoComplete: 'off',
                  className: 'text-right',
                  placeholder: '0',
                },
                endAdornment: (
                  <InputAdornment position='end'>
                    <span>บาท</span>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='เป้าหมายจำนวนการเปิดบิล / วัน'
              onChange={(e) => setAttr({ ...attr, target_purchase: e.target.value })}
              onFocus={(e) => e.target.select()}
              value={attr.target_purchase}
              variant='standard'
              type='number'
              fullWidth
              InputProps={{
                inputProps: {
                  maxLength: 20,
                  autoComplete: 'off',
                  className: 'text-right',
                  placeholder: '0',
                },
                endAdornment: (
                  <InputAdornment position='end'>
                    <span>บิล</span>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <Grid container mt={3}>
          <Grid item xs={12} display='flex' justifyContent='flex-end' gap={1}>
            <MDButton variant='outlined' color='success' onClick={editPurchase}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderWarehouse = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h6' mb={2}>
          คลังสินค้า
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='ปริมาณการโอนสินค้า ต่อรายการ'
              onChange={(e) => setAttr({ ...attr, quantity_export: e.target.value })}
              onFocus={(e) => e.target.select()}
              value={attr.quantity_export}
              variant='standard'
              type='number'
              fullWidth
              InputProps={{
                inputProps: {
                  maxLength: 20,
                  autoComplete: 'off',
                  className: 'text-right',
                  placeholder: '0',
                },
                endAdornment: (
                  <InputAdornment position='end'>
                    <span>ชิ้น</span>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <Grid container mt={3}>
          <Grid item xs={12} display='flex' justifyContent='flex-end' gap={1}>
            <MDButton variant='outlined' color='success' onClick={editWarehouse}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderSale = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h6' mb={2}>
          การขาย
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='หมายเหตุใบเสนอราคา'
              onChange={(e) => setAttr({ ...attr, quotation_remark: e.target.value })}
              value={attr.quotation_remark}
              variant='standard'
              fullWidth
              multiline
              InputProps={{
                inputProps: {
                  autoComplete: 'off',
                  placeholder: '',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='หมายเหตุใบแจ้งหนี้'
              onChange={(e) => setAttr({ ...attr, invoice_remark: e.target.value })}
              value={attr.invoice_remark || ''}
              variant='standard'
              fullWidth
              multiline
              InputProps={{
                inputProps: {
                  autoComplete: 'off',
                  placeholder: '',
                },
              }}
            />
          </Grid>
        </Grid>
        <Grid container mt={3}>
          <Grid item xs={12} display='flex' justifyContent='flex-end' gap={1}>
            <MDButton variant='outlined' color='success' onClick={editSale}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            <Card sx={{ p: 2 }}>
              <MDBox sx={{ width: '100%' }}>
                <MDBox>
                  <Tabs value={tab} onChange={(e, v) => setTab(v)} aria-label='tabs-addedit'>
                    <Tab label='บัญชี' />
                    <Tab label='จัดซื้อ' />
                    <Tab label='คลังสินค้า' />
                    <Tab label='การขาย' />
                  </Tabs>
                </MDBox>
                <TabPanel value={tab} index={0}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} xl={3}>
                      {renderAccount()}
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={tab} index={1}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} xl={3}>
                      {renderPurchase()}
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={tab} index={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} xl={3}>
                      {renderWarehouse()}
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={tab} index={3}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {renderSale()}
                    </Grid>
                  </Grid>
                </TabPanel>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.setting`), t(`menu.config`)]} />
      {renderOpen()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default Config;
